import { prohibitedPhrasesFor, type ProhibitedPhraseId, type WordingSurface } from './authority/ny/prohibitedPhrases';

// CODE-COMPLIANCE WORDING GUARD — the narrowest guard that works (NY F1 Round 4; frozen plan §14;
// RP-02 / AI-03 / CK-W1 … CK-W7).
//
// One versioned phrase table (src/domain/authority/ny/prohibitedPhrases.ts), two consumers:
//   1. the Scope Gate, step 4 — it scans the AI's REPORT-BOUND fields (observation, recommendation,
//      title, headline) and removes the offending SENTENCE, keeping the rest of the observation;
//   2. Final Review — it scans the inspector's own printed text and produces a NON-BLOCKING
//      reminder list (OWNER-CONTRACT §8.9 "提醒不阻断"). Report generation is never blocked here and
//      no inspector text is ever rewritten: the inspector decides.
//
// Never scanned: inspector-facing guidance (`guidance`, `checkSteps`, `concern`), checklist hints,
// the private item note and the AI scan note — building-code knowledge is explicitly allowed there
// (AI policy §4 closing paragraph).
//
// Pure: no store, no i18n, no network, no clock. The reminder carries the phrase id; the App
// Language wording of "what to write instead" lives in the two dictionaries.
export interface ProhibitedHit {
  phraseId: ProhibitedPhraseId;
  ruleIds: readonly string[];
  // The exact text that matched, and the sentence it sits in.
  match: string;
  sentence: string;
}

// Sentence boundaries in both languages. A period only ends a sentence when whitespace or the end
// of the text follows it, so "3.5 inches" and "197-5.8(d)" stay in one piece.
export function splitSentences(text: string): string[] {
  const out: string[] = [];
  let current = '';
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    current += ch;
    const next = i + 1 < text.length ? text[i + 1] : '';
    const ends = ch === '.' ? next === '' || /\s/.test(next) : '!?。！？；;\n'.includes(ch);
    if (ends) {
      out.push(current);
      current = '';
    }
  }
  if (current.length > 0) out.push(current);
  return out.filter((s) => s.trim().length > 0);
}

export function scanProhibited(text: string, surface: WordingSurface): ProhibitedHit[] {
  const hits: ProhibitedHit[] = [];
  if (text.trim().length === 0) return hits;
  for (const sentence of splitSentences(text)) {
    for (const phrase of prohibitedPhrasesFor(surface)) {
      for (const pattern of [...phrase.patterns.en, ...phrase.patterns.zh]) {
        const found = sentence.match(pattern);
        if (!found) continue;
        hits.push({ phraseId: phrase.id, ruleIds: phrase.ruleIds, match: found[0].trim(), sentence: sentence.trim() });
        break;
      }
    }
  }
  return hits;
}

// Sentence-level rejection, never a paragraph deleter (plan §14): the sentences that carry a
// prohibited conclusion are removed and the rest of the text is preserved exactly.
export function stripProhibitedSentences(text: string, surface: WordingSurface): { text: string; hits: ProhibitedHit[]; removed: string[] } {
  if (text.trim().length === 0) return { text, hits: [], removed: [] };
  const kept: string[] = [];
  const removed: string[] = [];
  const hits: ProhibitedHit[] = [];
  for (const sentence of splitSentences(text)) {
    const sentenceHits = scanProhibited(sentence, surface);
    if (sentenceHits.length === 0) {
      kept.push(sentence);
      continue;
    }
    hits.push(...sentenceHits);
    removed.push(sentence.trim());
  }
  return { text: kept.join('').trim(), hits, removed };
}

// ---------- Final Review (RP-02) ----------

// Where a printed string came from. Only text the INSPECTOR wrote and the report PRINTS is listed
// here; product wording (labels, the frozen limitation templates, the scope statement, the
// agreement terms) is never scanned, and neither is anything that stays inside the app.
export type WordingSource =
  | 'reportSentence'
  | 'findingTitle'
  | 'findingObservation'
  | 'findingRecommendation'
  | 'findingTrade'
  | 'limitationNote'
  | 'portionExtent'
  | 'portionNote'
  | 'notPresentNote'
  | 'areaName'
  | 'areaNote'
  | 'itemTitle'
  | 'descriptorValue'
  | 'photoCaption'
  | 'inspectorNotes'
  | 'weather';

export interface ReportBoundText {
  source: WordingSource;
  text: string;
  areaId: string | null;
  itemId: string | null;
  findingId: string | null;
}

export interface WordingReminder extends ProhibitedHit {
  source: WordingSource;
  areaId: string | null;
  itemId: string | null;
  findingId: string | null;
}

// The non-blocking Final Review list. Input = exactly what the report will print from the
// inspector's own text (src/report/assembly.ts `reportBoundInspectorText`).
export function wordingRemindersFor(texts: readonly ReportBoundText[]): WordingReminder[] {
  const out: WordingReminder[] = [];
  for (const entry of texts) {
    for (const hit of scanProhibited(entry.text, 'report')) {
      out.push({ ...hit, source: entry.source, areaId: entry.areaId, itemId: entry.itemId, findingId: entry.findingId });
    }
  }
  return out;
}
